import { useEffect, useState } from 'react';
import { Save, Percent, DollarSign, Mail, Phone, ShieldCheck } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Field, Input, Checkbox, Button, StatusLine,
} from '../components/ui';

type Settings = {
  id: number;
  platform_commission_rate: number;
  coach_platform_fee_rate:  number;
  security_deposit_amount:  number;
  verified_deposit_waived:  boolean;
  support_email:            string | null;
  support_phone:            string | null;
};

/**
 * Platform-wide knobs. Only fields; saving writes the single row in
 * platform_settings. Customer-facing consumers (deposit amount, support
 * contacts) can read this table anonymously; commission/fee fields are
 * admin-writable and read by the edge functions via service role.
 */
export default function Settings() {
  const [s, setS] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('platform_settings').select('*').eq('id', 1).maybeSingle();
      setS(data as Settings);
    })();
  }, []);

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) =>
    setS((prev) => (prev ? { ...prev, [k]: v } : prev));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!s) return;
    setBusy(true); setMsg(null);

    const { data, error } = await supabase
      .from('platform_settings')
      .update({
        platform_commission_rate: s.platform_commission_rate,
        coach_platform_fee_rate:  s.coach_platform_fee_rate,
        security_deposit_amount:  s.security_deposit_amount,
        verified_deposit_waived:  s.verified_deposit_waived,
        support_email:            s.support_email || null,
        support_phone:            s.support_phone || null,
        updated_at:               new Date().toISOString(),
      })
      .eq('id', 1)
      .select('id');

    setBusy(false);
    if (error) return setMsg({ kind: 'error', text: error.message });
    if (!data || data.length === 0) {
      return setMsg({ kind: 'error', text: "Nothing was saved — you're not an admin." });
    }
    setMsg({ kind: 'success', text: 'Settings saved.' });
  };

  if (!s) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Platform settings"
        subtitle="Tune commission rates, deposit policy, and support contacts."
      />

      <form onSubmit={save}>
        <Section title="Commissions" subtitle="Applied to each booking on the platform side.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Club commission rate" hint="Platform's cut of court bookings. E.g. 0.12 = 12%.">
              <div className="relative">
                <Input
                  type="number" min="0" max="1" step="0.01"
                  value={String(s.platform_commission_rate)}
                  onChange={(e) => set('platform_commission_rate', Number(e.target.value))}
                />
                <Percent className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </Field>
            <Field label="Coach platform fee" hint="Platform's cut of coaching lessons.">
              <div className="relative">
                <Input
                  type="number" min="0" max="1" step="0.01"
                  value={String(s.coach_platform_fee_rate)}
                  onChange={(e) => set('coach_platform_fee_rate', Number(e.target.value))}
                />
                <Percent className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </Field>
          </div>
        </Section>

        <Section
          title={<span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2d5016]" /> Security deposit</span>}
          subtitle="Charged on rental bookings."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Deposit amount (AED)">
              <div className="relative">
                <DollarSign className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="number" min="0" step="10"
                  value={String(s.security_deposit_amount)}
                  onChange={(e) => set('security_deposit_amount', Number(e.target.value))}
                  className="pl-9"
                />
              </div>
            </Field>
            <div className="flex items-center">
              <Checkbox
                checked={s.verified_deposit_waived}
                onChange={(v) => set('verified_deposit_waived', v)}
                label="Waive deposit for ID-verified customers"
                hint="Customers who complete identity verification skip the deposit at checkout."
              />
            </div>
          </div>
        </Section>

        <Section title="Support contacts" subtitle="Shown on Help & Support screens and emails.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Support email">
              <div className="relative">
                <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="email"
                  value={s.support_email ?? ''}
                  onChange={(e) => set('support_email', e.target.value)}
                  className="pl-9"
                  placeholder="support@acerentdubai.ae"
                />
              </div>
            </Field>
            <Field label="Support phone">
              <div className="relative">
                <Phone className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="tel"
                  value={s.support_phone ?? ''}
                  onChange={(e) => set('support_phone', e.target.value)}
                  className="pl-9"
                  placeholder="+971 …"
                />
              </div>
            </Field>
          </div>
        </Section>

        {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

        <div>
          <Button type="submit" loading={busy} icon={Save}>Save settings</Button>
        </div>
      </form>
    </div>
  );
}
